
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { closeMenu } from "../../features/menu/menuSlice";
import Search from "../../components/Search";
import { convertApiToNavData } from "../../helpers/convertApiToNavData";
import { useTranslation } from "react-i18next";
import UpdateIcon from "../../../src/icons-svg/update_icon.svg";
import PartnerApi from "../../helpers/PartnerApi";

const Navigation = ({ docList, docInfo, onDocSelect, openLicenseModal, language }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { open } = useSelector((state) => state.menu);
  const menuRef = useRef(null);
  const [navData, setNavData] = useState([]);
  const [search, setSearch] = useState("");

  // normalizeString fonksiyonu
  const normalizeString = (str) => {
    if (!str) return "";
    return str
      .normalize("NFD")                // aksanları ayır
      .replace(/[\u0300-\u036f]/g, "") // aksanları kaldır
      .replace(/[’'`]/g, "")           // tek tırnak/akcentleri kaldır
      .replace(/\s+/g, "")             // boşlukları kaldır
      .toLowerCase();                  // küçük harf
  };

  // Menü verilerini dönüştür
  useEffect(() => {
    if ((!docList || docList.length === 0) && !docInfo) {
      setNavData([]);
      return;
    }

    let converted = [];
    if (docList && docList.length > 0) {
      converted = convertApiToNavData({ docSearchList: docList }, t, language);
    } else if (docInfo) {
      converted = convertApiToNavData(docInfo, t, language);
    }

    setNavData(converted);
  }, [docList, docInfo, language, t]);

  // Arama ve filtreleme
  const filterNav = (nodes) => {
    return nodes
      .map((node) => {
        const filteredChilds = node.childs ? filterNav(node.childs) : [];

        const matchesTitle = normalizeString(node.title).includes(normalizeString(search));
        const matchesKeyword = normalizeString(node.keyword || "").includes(normalizeString(search));

        if (matchesTitle || matchesKeyword) return { ...node };
        if (filteredChilds.length > 0) return { ...node, childs: filteredChilds };
        return null;
      })
      .filter(Boolean);
  };

  const filteredNavData = useMemo(() => filterNav(navData), [navData, search]);

  // Menü içerik durumu
  const renderMenuContent = () => {
    if (!navData || navData.length === 0) {
      return <p className="text-gray-500 text-sm italic">{t("Henüz menü verisi yok")}</p>;
    }

    if (search && filteredNavData.length === 0) {
      return <p className="text-gray-500 text-sm italic">{t("İçerik bulunamadı")}</p>;
    }

    return (
      <ul style={{ listStyle: "none", paddingLeft: 0 }}>
        {renderMenu(filteredNavData)}
      </ul>
    );
  };

  // Menü render
  const renderMenu = (nodes) =>
    nodes.map((node) => (
      <li key={node.id} style={{ marginBottom: 8 }}>
        {node.uuid ? (
          <span
            onClick={() => onDocSelect({ uuid: node.uuid, title: node.title })}
            className="inline-block rounded cursor-pointer transition hover:bg-gray-100 font-normal hover:font-bold text-[#4C67E9] text-[14px]"
          >
            {node.title}
          </span>
        ) : (
          <strong style={{ fontSize: "16px" }}>{node.title}</strong>
        )}
        {node.childs?.length > 0 && (
          <ul style={{ paddingLeft: 20, listStyle: "none" }}>
            {renderMenu(node.childs)}
          </ul>
        )}
      </li>
    ));

  // Dış tıklamada menüyü kapatma
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) dispatch(closeMenu());
    };

    if (open) {
      document.body.style.overflow = "hidden";
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "";
    };
  }, [open, dispatch]);

  return (
    <>
      <div
        ref={menuRef}
        className={`fixed lg:static z-50 top-0 left-0 h-full w-72 bg-[#F0F3F4] transition-transform lg:translate-x-0 overflow-hidden flex flex-col ${open ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        {/* Üst alan */}
        <div className="sticky top-0 bg-[#F0F3F4] px-4 pt-3 pb-3 z-10 border-b border-gray-300">
          <div className="mb-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">
                {t("Software")}:{" "}
                <span className="font-normal">
                  {docInfo?.licenseDocInfo?.licenseProductName || "-"}
                </span>
              </div>
              <img
                src={UpdateIcon}
                alt={t("Update")}
                className="w-5 h-5 object-contain cursor-pointer ml-2"
                onClick={openLicenseModal}
              />
            </div>
            <div className="text-sm font-semibold mt-1">
            {t("Version")}:{" "}
              <span className="font-normal">{docInfo?.licenseDocInfo?.licenseCurrentVersion || "-"}</span>
            </div>
          </div>
          <Search
            onSearch={setSearch}
            placeholder={t("Ara...")}
            value={search}
            className="rounded px-2 py-1"
            style={{
              backgroundColor: "#ffffff",
              color: "#000000",
              border: "1px solid #C4CED8",
            }}
          />
        </div>

        {/* Scrollable menü */}
        <nav className="flex-1 overflow-y-auto p-2" style={{ minHeight: 0 }}>
          {renderMenuContent()}
        </nav>

        {/* Alt sabit alan */}
        <div className="px-4 py-2 bg-gray-200 text-gray-800 font-semibold border-t border-gray-300 mt-auto">
          V1.0.5
        </div>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => dispatch(closeMenu())}
        />
      )}
    </>
  );
};

export default Navigation;
